import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Image, Plus, Edit, Trash2, ArrowLeft, Eye, MousePointer, BarChart2, ExternalLink,
  Calendar, Power, AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const BannerAdsManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    position: 'home_top',
    start_date: '',
    end_date: '',
    enabled: true,
    priority: 0
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const positions = [
    { value: 'home_top', label: 'Home Page - Top Banner' },
    { value: 'home_sidebar', label: 'Home Page - Sidebar' },
    { value: 'category_top', label: 'Category Page - Top' },
    { value: 'listing_detail', label: 'Listing Detail - Below Images' },
    { value: 'search_results', label: 'Search Results - Top' }
  ];

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/admin/ads/banners?include_inactive=true`,
        { headers: getAuthHeader() }
      );
      setBanners(response.data.banners || []);
    } catch (err) {
      console.error('Error fetching banners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.image_url.trim()) {
      alert('Title and Image URL are required');
      return;
    }

    try {
      const payload = {
        title: formData.title,
        image_url: formData.image_url,
        link_url: formData.link_url || null,
        position: formData.position,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        enabled: formData.enabled,
        priority: parseInt(formData.priority) || 0
      };

      if (editingBanner) {
        await axios.put(
          `${API_URL}/api/admin/ads/banners/${editingBanner.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        alert('Banner updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/admin/ads/banners`,
          payload,
          { headers: getAuthHeader() }
        );
        alert('Banner created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchBanners();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save banner');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/ads/banners/${bannerId}`,
        { headers: getAuthHeader() }
      );
      alert('Banner deleted successfully');
      fetchBanners();
    } catch (err) {
      alert('Failed to delete banner');
    }
  };

  const handleToggle = async (bannerId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/ads/banners/${bannerId}/toggle`,
        {},
        { headers: getAuthHeader() }
      );
      fetchBanners();
    } catch (err) {
      alert('Failed to toggle banner');
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      image_url: banner.image_url,
      link_url: banner.link_url || '',
      position: banner.position,
      start_date: banner.start_date ? banner.start_date.split('T')[0] : '',
      end_date: banner.end_date ? banner.end_date.split('T')[0] : '',
      enabled: banner.enabled,
      priority: banner.priority || 0
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingBanner(null);
    setFormData({
      title: '',
      image_url: '',
      link_url: '',
      position: 'home_top',
      start_date: '',
      end_date: '',
      enabled: true,
      priority: 0
    });
  };

  const getPositionLabel = (position) => {
    const pos = positions.find(p => p.value === position);
    return pos ? pos.label : position;
  };

  const groupedBanners = positions.map(pos => ({
    ...pos,
    banners: banners.filter(b => b.position === pos.value)
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/admin/ads">
                <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Image className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Banner Ads</h1>
                <p className="text-purple-100 text-sm">Manage promotional banners across the site</p>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-white text-purple-600 hover:bg-gray-100">
              <Plus className="h-4 w-4 mr-2" />
              Add Banner
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Banners</p>
                  <p className="text-3xl font-bold">{banners.length}</p>
                </div>
                <Image className="h-10 w-10 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active</p>
                  <p className="text-3xl font-bold text-green-600">
                    {banners.filter(b => b.enabled && !b.is_expired).length}
                  </p>
                </div>
                <Power className="h-10 w-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Impressions</p>
                  <p className="text-3xl font-bold">
                    {banners.reduce((sum, b) => sum + (b.impressions || 0), 0).toLocaleString()}
                  </p>
                </div>
                <Eye className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Clicks</p>
                  <p className="text-3xl font-bold">
                    {banners.reduce((sum, b) => sum + (b.clicks || 0), 0).toLocaleString()}
                  </p>
                </div>
                <MousePointer className="h-10 w-10 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Banners by Position */}
        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading banners...</p>
        ) : (
          <div className="space-y-6">
            {groupedBanners.map(group => (
              <Card key={group.value}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    {group.label}
                    <Badge variant="secondary">{group.banners.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {group.banners.length === 0 ? (
                    <p className="text-center py-8 text-gray-400">No banners in this position</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {group.banners.map(banner => (
                        <div
                          key={banner.id}
                          className={`border rounded-lg overflow-hidden ${!banner.enabled ? 'opacity-50' : ''}`}
                        >
                          <div className="relative aspect-video bg-gray-100">
                            <img
                              src={banner.image_url}
                              alt={banner.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            {banner.is_expired && (
                              <div className="absolute top-2 left-2">
                                <Badge variant="destructive">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Expired
                                </Badge>
                              </div>
                            )}
                            {!banner.enabled && (
                              <div className="absolute top-2 right-2">
                                <Badge variant="secondary">Disabled</Badge>
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold mb-2">{banner.title}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                              <span className="flex items-center gap-1">
                                <Eye className="h-4 w-4" />
                                {(banner.impressions || 0).toLocaleString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <MousePointer className="h-4 w-4" />
                                {(banner.clicks || 0).toLocaleString()}
                              </span>
                              {banner.impressions > 0 && (
                                <span className="flex items-center gap-1">
                                  <BarChart2 className="h-4 w-4" />
                                  {((banner.clicks / banner.impressions) * 100).toFixed(1)}% CTR
                                </span>
                              )}
                            </div>
                            {banner.link_url && (
                              <a
                                href={banner.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1 mb-3"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {banner.link_url.substring(0, 30)}...
                              </a>
                            )}
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleToggle(banner.id)}
                              >
                                <Power className="h-4 w-4 mr-1" />
                                {banner.enabled ? 'Disable' : 'Enable'}
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleEdit(banner)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleDelete(banner.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Banner Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingBanner ? 'Edit Banner' : 'Create Banner Ad'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Banner title (internal use)"
              />
            </div>

            <div>
              <Label>Image URL *</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/banner.jpg"
              />
              {formData.image_url && (
                <div className="mt-2 border rounded-lg overflow-hidden">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="max-h-40 w-full object-contain bg-gray-100"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div>
              <Label>Link URL (optional)</Label>
              <Input
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                placeholder="https://example.com/landing-page"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Position *</Label>
                <Select
                  value={formData.position}
                  onValueChange={(value) => setFormData({ ...formData, position: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map(pos => (
                      <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Enabled</Label>
                <p className="text-sm text-gray-500">Banner will be visible immediately</p>
              </div>
              <Switch
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>{editingBanner ? 'Update Banner' : 'Create Banner'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannerAdsManagement;
